import * as React from "react";
import { useState, useEffect } from "react";
import { 
  Database, RefreshCw, CheckCircle2, AlertTriangle, XCircle, 
  Pause, Clock, ExternalLink, Activity, Zap, AlertOctagon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const StatusBadge = ({ status }) => {
  const config = {
    HEALTHY: { color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2, label: "Saludable" },
    WARNINGS: { color: "bg-amber-100 text-amber-700 border-amber-300", icon: AlertTriangle, label: "Advertencia" },
    HIGH_WARNINGS: { color: "bg-amber-100 text-amber-700 border-amber-300", icon: AlertTriangle, label: "Muchas Advertencias" },
    DELAYED: { color: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock, label: "Retrasado" },
    PAUSED: { color: "bg-red-100 text-red-700 border-red-300", icon: Pause, label: "Pausado (Crítico)" },
    NO_ACTIVITY: { color: "bg-slate-100 text-slate-500 border-slate-300", icon: Clock, label: "Sin Actividad" },
  };
  
  const { color, icon: Icon, label } = config[status] || config.NO_ACTIVITY;
  
  return (
    <Badge className={`${color} border flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
};

// Categorize status for summary
const categorizeStatus = (status) => {
  if (status === "PAUSED") return "critical";
  if (["WARNINGS", "HIGH_WARNINGS", "DELAYED"].includes(status)) return "warning";
  if (status === "HEALTHY") return "healthy";
  return "inactive";
};

export const FivetranStatusCard = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/fivetran/status`);
      setData(response.data);
    } catch (err) {
      console.error("Error fetching Fivetran status:", err);
      setError("No se pudo conectar con Fivetran");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatFrequency = (minutes) => {
    if (!minutes) return "N/A";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Recalculate summary with new categorization
  const calculateSummary = (connections) => {
    const summary = { total: 0, healthy: 0, warning: 0, critical: 0, inactive: 0 };
    connections?.forEach(conn => {
      summary.total++;
      const category = categorizeStatus(conn.status);
      summary[category]++;
    });
    return summary;
  };

  if (isLoading) {
    return (
      <Card className="border-2 border-dashed border-slate-200">
        <CardContent className="py-12 text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500">Consultando estado de conectores...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-2 border-red-200 bg-red-50">
        <CardContent className="py-8 text-center">
          <XCircle className="h-8 w-8 text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchStatus} className="mt-3">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  const summary = calculateSummary(data?.connections);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="py-4 text-center">
            <Database className="h-6 w-6 text-slate-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-slate-700">{summary.total}</p>
            <p className="text-xs text-slate-500">Total Conectores</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="py-4 text-center">
            <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-emerald-600">{summary.healthy}</p>
            <p className="text-xs text-emerald-600">Saludables</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 text-center">
            <AlertTriangle className="h-6 w-6 text-amber-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-amber-600">{summary.warning}</p>
            <p className="text-xs text-amber-600">Con Advertencias</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4 text-center">
            <Pause className="h-6 w-6 text-red-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-red-600">{summary.critical}</p>
            <p className="text-xs text-red-600">Críticos (Pausados)</p>
          </CardContent>
        </Card>
      </div>

      {/* Connectors List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-slate-500" />
                Lista de Conectores
              </CardTitle>
              <CardDescription>
                Estado detallado de cada conector de datos
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchStatus}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data?.connections?.map((conn, idx) => {
              const category = categorizeStatus(conn.status);
              const borderColor = category === "critical" ? "border-l-red-500" 
                : category === "warning" ? "border-l-amber-500"
                : category === "healthy" ? "border-l-emerald-500"
                : "border-l-slate-300";
              
              return (
                <div 
                  key={`${conn.connection_id}-${idx}`}
                  className={`p-4 rounded-lg border border-l-4 ${borderColor} bg-white hover:bg-slate-50 transition-colors`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Name and Type */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-slate-800">
                          {conn.connection_name}
                        </h4>
                        <StatusBadge status={conn.status} />
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Database className="h-3.5 w-3.5" />
                          {conn.connector_type || "Desconocido"}
                        </span>
                        <span className="text-slate-300">|</span>
                        <span>{conn.catalog}</span>
                      </div>
                    </div>
                    
                    {/* Right: Sync Info */}
                    <div className="text-right text-sm">
                      <div className="text-slate-600">
                        <span className="text-slate-400">Última sync: </span>
                        <span className="font-medium">{formatDate(conn.last_sync)}</span>
                      </div>
                      <div className="text-slate-500 mt-1">
                        <span className="text-slate-400">Frecuencia: </span>
                        <span>{formatFrequency(conn.sync_frequency_minutes)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom: Additional Info */}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs">
                      {conn.warnings_24h > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`flex items-center gap-1 ${conn.warnings_24h > 10 ? 'text-amber-600' : 'text-amber-500'}`}>
                                <AlertTriangle className="h-3.5 w-3.5" />
                                {conn.warnings_24h} warnings (24h)
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Advertencias en las últimas 24 horas</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {conn.minutes_since_last_sync > 0 && (
                        <span className="text-slate-400">
                          Hace {conn.minutes_since_last_sync} min
                        </span>
                      )}
                    </div>
                    {conn.next_sync && (
                      <span className="text-xs text-slate-400">
                        Próxima sync: {formatDate(conn.next_sync)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Footer Info */}
      <div className="text-center text-xs text-slate-400">
        <p>Última consulta: {formatDate(data?.queried_at)}</p>
        <p className="mt-1">Datos obtenidos automáticamente desde las tablas de metadatos de Fivetran en Databricks</p>
      </div>
    </div>
  );
};
